<template>
  <v-btn
    :style="
      (variant === 'transparent' || variant === 'ghost' || variant === 'primary' || variant === 'utility') && color && !disabled
        ? { '--rmr-btn-color': color }
        : {}
    "
    :loading="loading"
    :disabled="disabled"
    :depressed="variant !== 'translucent'"
    class="rmr-action-btn"
    :class="[
      `rmr-action-btn--${variant}`,
      `rmr-action-btn--size-${size}`,
      { 'rmr-action-btn--active': active },
    ]"
    :type="type"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <v-icon
      v-if="icon"
      left
      class="rmr-action-btn__icon"
      :class="[
        `rmr-action-btn__icon--size-${size}`,
        { 'rmr-action-btn__icon--spinning': spinIcon },
      ]"
    >
      {{ icon }}
    </v-icon>
    {{ label }}
  </v-btn>
</template>

<script>
export default {
  name: "RmrActionButton",
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "primary",
      validator: (value) =>
        ["primary", "translucent", "transparent", "ghost", "utility"].includes(value),
    },
    color: {
      type: String,
      default: null,
    },
    active: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["xs", "sm", "md", "lg"].includes(value),
    },
    spinIcon: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "button",
    },
  },
};
</script>

<style lang="scss" scoped>
.rmr-action-btn {
  font-weight: 700 !important;
  border-radius: $rmr-radius-md !important;
  transition: all 0.2s ease !important;

  &--primary {
    background: linear-gradient(
      135deg,
      var(--rmr-btn-color, #{$rmr-indigo}) 0%,
      var(--rmr-btn-color, #{$rmr-rpm}) 100%
    ) !important;
    color: white !important;
    text-transform: uppercase !important;
    font-size: 11px !important;
    letter-spacing: 0.8px !important;
    height: 38px !important;
    padding: 0 24px !important;
    box-shadow: 0 4px 14px rgba($rmr-indigo, 0.35) !important;

    &:hover:not(.v-btn--disabled) {
      box-shadow: 0 6px 20px rgba($rmr-indigo, 0.5) !important;
      transform: translateY(-1px);
    }

    &.v-btn--disabled {
      background: linear-gradient(135deg, $rmr-indigo 0%, $rmr-rpm 100%) !important;
      opacity: 0.6 !important;
      color: white !important;
      pointer-events: auto !important;
      cursor: not-allowed !important;
      box-shadow: none !important;

      ::v-deep .v-btn__content {
        color: white !important;
      }

      ::v-deep .v-icon {
        color: white !important;
      }
    }

    &.v-btn--loading {
      pointer-events: none !important;
    }

    ::v-deep .v-btn__loader {
      color: white !important;
    }
  }

  &--translucent {
    background: rgba(255, 255, 255, 0.1) !important;
    border: 1px solid rgba(255, 255, 255, 0.12) !important;
    color: white !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    letter-spacing: 0.2px !important;
    text-transform: none !important;
    height: 42px !important;
    padding: 0 20px !important;
    box-shadow: none !important;

    &:hover:not(.v-btn--disabled):not(.rmr-action-btn--active) {
      background: rgba(255, 255, 255, 0.16) !important;
      border-color: rgba(255, 255, 255, 0.18) !important;
    }

    &.v-btn--disabled {
      opacity: 0.6 !important;
      pointer-events: auto !important;
      cursor: not-allowed !important;
    }

    .v-icon {
      color: white !important;
    }
  }

  &--transparent {
    background: transparent !important;
    border: 1px solid var(--rmr-btn-color, #{$rmr-text-secondary}) !important;
    color: var(--rmr-btn-color, #{$rmr-text-secondary}) !important;
    box-shadow: none !important;
    text-transform: none !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    height: 42px !important;
    padding: 0 20px !important;

    ::v-deep .v-btn__content {
      color: var(--rmr-btn-color, #{$rmr-text-secondary}) !important;
    }

    .v-icon {
      color: var(--rmr-btn-color, #{$rmr-text-secondary}) !important;
    }

    &:hover:not(.v-btn--disabled) {
      background: var(--rmr-btn-color, #{$rmr-text-secondary}) !important;
      color: white !important;
      border-color: var(--rmr-btn-color, #{$rmr-text-secondary}) !important;

      ::v-deep .v-btn__content {
        color: white !important;
      }

      .v-icon {
        color: white !important;
      }
    }

    &.v-btn--disabled {
      opacity: 0.5 !important;
      pointer-events: auto !important;
      cursor: not-allowed !important;
    }
  }

  &--ghost {
    background: transparent !important;
    box-shadow: none !important;
    color: var(--rmr-btn-color, #{$rmr-text-secondary}) !important;
    text-transform: none !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    height: 38px !important;
    padding: 0 16px !important;

    ::v-deep .v-btn__content {
      color: var(--rmr-btn-color, #{$rmr-text-secondary}) !important;
    }

    .v-icon {
      color: var(--rmr-btn-color, #{$rmr-text-secondary}) !important;
    }

    &:hover:not(.v-btn--disabled) {
      background: rgba(0, 0, 0, 0.04) !important;
    }

    &.v-btn--disabled {
      opacity: 0.4 !important;
      pointer-events: auto !important;
      cursor: not-allowed !important;
    }
  }

  &--utility {
    background: color-mix(in srgb, var(--rmr-btn-color, #{$rmr-danger}) 12%, white) !important;
    border: 1px solid transparent !important;
    box-shadow: none !important;
    color: var(--rmr-btn-color, #{$rmr-danger}) !important;
    text-transform: none !important;
    font-size: 10px !important;
    font-weight: 700 !important;
    letter-spacing: 0 !important;
    height: 28px !important;
    padding: 0 10px !important;
    border-radius: $rmr-radius-pill !important;

    ::v-deep .v-btn__content {
      color: var(--rmr-btn-color, #{$rmr-danger}) !important;
    }

    .v-icon {
      color: var(--rmr-btn-color, #{$rmr-danger}) !important;
    }

    &:hover:not(.v-btn--disabled) {
      background: color-mix(in srgb, var(--rmr-btn-color, #{$rmr-danger}) 18%, white) !important;
      transform: translateY(-1px);
    }

    &.v-btn--disabled {
      opacity: 0.45 !important;
      pointer-events: auto !important;
      cursor: not-allowed !important;
    }
  }

  &--translucent.rmr-action-btn--active {
    background: $rmr-green !important;
    border-color: $rmr-green !important;
    color: $rmr-surface-0 !important;

    .v-icon {
      color: $rmr-surface-0 !important;
    }
  }

  &--size-sm {
    height: 36px !important;
    padding: 0 18px !important;
  }

  &--size-xs {
    height: 30px !important;
    padding: 0 12px !important;
  }

  &--primary.rmr-action-btn--size-sm {
    height: 34px !important;
    padding: 0 18px !important;
    font-size: 10px !important;
    letter-spacing: 0.6px !important;
  }

  &--primary.rmr-action-btn--size-xs {
    height: 28px !important;
    padding: 0 12px !important;
    font-size: 9px !important;
    letter-spacing: 0.5px !important;
  }

  &--primary.rmr-action-btn--size-lg {
    height: 44px !important;
    padding: 0 28px !important;
    font-size: 12px !important;
    letter-spacing: 0.9px !important;
  }

  &--translucent.rmr-action-btn--size-sm,
  &--transparent.rmr-action-btn--size-sm {
    height: 38px !important;
    padding: 0 16px !important;
    font-size: 11px !important;
  }

  &--translucent.rmr-action-btn--size-xs,
  &--transparent.rmr-action-btn--size-xs {
    height: 32px !important;
    padding: 0 12px !important;
    font-size: 10px !important;
  }

  &--translucent.rmr-action-btn--size-lg,
  &--transparent.rmr-action-btn--size-lg {
    height: 46px !important;
    padding: 0 24px !important;
    font-size: 13px !important;
  }

  &--ghost.rmr-action-btn--size-sm {
    height: 34px !important;
    padding: 0 14px !important;
    font-size: 11px !important;
  }

  &--ghost.rmr-action-btn--size-xs {
    height: 28px !important;
    padding: 0 10px !important;
    font-size: 10px !important;
  }

  &--ghost.rmr-action-btn--size-lg {
    height: 42px !important;
    padding: 0 18px !important;
    font-size: 13px !important;
  }

  &--utility.rmr-action-btn--size-sm {
    height: 28px !important;
    padding: 0 10px !important;
    font-size: 10px !important;
  }

  &--utility.rmr-action-btn--size-xs {
    height: 24px !important;
    padding: 0 8px !important;
    font-size: 9px !important;
  }

  &--utility.rmr-action-btn--size-lg {
    height: 32px !important;
    padding: 0 12px !important;
    font-size: 11px !important;
  }
}

.rmr-action-btn__icon--size-xs {
  font-size: 14px !important;
}

.rmr-action-btn__icon--size-sm {
  font-size: 16px !important;
}

.rmr-action-btn__icon--size-md {
  font-size: 18px !important;
}

.rmr-action-btn__icon--size-lg {
  font-size: 20px !important;
}

.rmr-action-btn--utility .rmr-action-btn__icon--size-xs {
  font-size: 12px !important;
}

.rmr-action-btn--utility .rmr-action-btn__icon--size-sm {
  font-size: 13px !important;
}

.rmr-action-btn__icon--spinning {
  animation: rmr-action-btn-spin 1s linear infinite;
}

@keyframes rmr-action-btn-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
